import React from 'react';
import { useCopilot } from './CopilotContext';
import useUserProfile from 'src/hooks/useUserProfile';
import { Persona } from 'src/contstant/Persona';
import { CURRENT_STAGE, Stage } from 'src/contstant/apiEndpoints';
import { ASTRA_ICON_URL } from './brand';
import './Copilot.scss';

// Feature flag: show copilot in beta, gamma (and test/local dev)
const COPILOT_ENABLED_STAGES: Set<Stage> = new Set([Stage.TEST, Stage.BETA, Stage.GAMMA]);

// TEMPORARY prod allowlist: show Astra in prod for these users while it is being rolled out.
// Add aliases here to grant early access; remove this list (and the prod carve-out in
// isCopilotVisibleForUser) once Astra is fully enabled in prod.
const PROD_ALLOWLISTED_USERS: Set<string> = new Set([
  'oggurram',
  'shpragy',
  'coglis',
  'sailattu',
  'ssdivya',
  'mrypshah',
  'tjmoats',
  'kukumpi',
]);

// TEMPORARY prod role allowlist: any user whose role (persona) is in this set sees Astra in prod.
// Widening the rollout to another role is a one-line add here — no need to list individual users.
const PROD_ALLOWLISTED_ROLES: Set<string> = new Set<string>([Persona.PLATFORM_ADMIN]);

/**
 * Whether the Astra trigger should be shown for the given stage, user, and role.
 *
 * @param stage the current deployment stage
 * @param userId the current user's alias
 * @param persona the current user's role (e.g. {@link Persona.PLATFORM_ADMIN}); optional
 * @returns true if the Astra launcher should be visible
 */
export function isCopilotVisibleForUser(
  stage: Stage,
  userId: string,
  persona = '',
): boolean {
  if (COPILOT_ENABLED_STAGES.has(stage)) return true;
  // TEMPORARY: prod is gated to the allowlisted users and roles until Astra is fully enabled.
  return (
    stage === Stage.PROD &&
    (PROD_ALLOWLISTED_USERS.has(userId) || PROD_ALLOWLISTED_ROLES.has(persona))
  );
}

interface CopilotTriggerProps {
  isAuthenticated: boolean;
}

export function CopilotTrigger({ isAuthenticated }: CopilotTriggerProps) {
  const { state, userId, togglePanel } = useCopilot();
  const { getPersona } = useUserProfile();

  if (!isCopilotVisibleForUser(CURRENT_STAGE, userId, getPersona())) return null;
  if (!isAuthenticated || state.isOpen) return null;

  // Custom FAB instead of a Cloudscape Button: Button caps iconUrl at 16px, too small for
  // the launcher. Image is decorative (alt=""); accessible name comes from aria-label.
  return (
    <div className="copilot-trigger">
      <button
        type="button"
        className="copilot-trigger-button"
        onClick={togglePanel}
        aria-label="Open Astra"
      >
        <img className="copilot-trigger-icon" src={ASTRA_ICON_URL} alt="" />
      </button>
    </div>
  );
}
